import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import Header from "../../CoreFile/Header";
import Footer from "../../CoreFile/Footer";
import { AboutHeroSection } from "./AboutHeroSection";
import { AboutSection } from "../AboutSection";
import { Overview } from "../Overview";

export const About = () => {
  const stats = [
    {
      number: "50K+",
      label: "Active Forex Traders",
      description: "A growing global community trusts GFM for smarter trading.",
    },
    {
      number: "100+",
      label: "Supported Currency Pairs",
      description: "Trade all major, minor, and exotic pairs with ease.",
    },
    {
      number: "$500M+",
      label: " Monthly Trading Volume",
      description: "High-volume trading powered by smart automation.",
    },
    {
      number: "99.9%",
      label: "  Bot Uptime & System Reliability",
      description: "Trade with confidence—anytime, anywhere.",
    },
  ];



  const fadeIn = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  const shapeRef = useRef(null);

  useEffect(() => {
    const rotateShape = () => {
      if (shapeRef.current) {
        // Increased rotation speed by multiplying the time factor by 8
        shapeRef.current.style.transform = `rotate(${
          ((Date.now() / 1000) * 8) % 360
        }deg)`;
        requestAnimationFrame(rotateShape);
      }
    };

    const animationId = requestAnimationFrame(rotateShape);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, []);
  return (
    <>
      <Header />
      <AboutHeroSection />
<Overview/>
      <motion.div
      className="bg-gradient-to-br from-[#263283] to-[#ed2924] py-8 px-6 sm:px-10 lg:px-20 relative overflow-hidden"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      variants={fadeIn}
    >
    <AboutSection/>

      {/* Background Gradient Blur Circles */}
      <div className="absolute w-40 h-40 bg-pink-500 rounded-full blur-3xl opacity-20 -bottom-10 -left-10" />
      <div className="absolute w-64 h-64 bg-blue-500 rounded-full blur-3xl opacity-20 -top-16 -right-16" />
    </motion.div>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            variants={fadeIn}
          >
            <h2 className="text-3xl font-bold text-gray-900">
            Trusted by Traders Worldwide
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            We’re proud to be a reliable choice for thousands of traders across the world. Our results and community speak for themselves.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-4 sm:grid-cols-2 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="bg-gray-50 rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all border border-gray-100"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                <h3 className="text-4xl font-bold text-red-600 mb-2">
                  {stat.number}
                </h3>
                <p className="text-xl font-semibold text-gray-800 mb-3">
                  {stat.label}
                </p>
                <p className="text-gray-600">{stat.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 relative">
        {/* Background image with overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://img.freepik.com/premium-photo/online-trading-concept-with-business-financial-chart-with-graphs-laptop-screen-brown-wooden-table-with-pen-diary-lamp-3d-rendering_670147-53576.jpg?uid=R180299756&ga=GA1.1.815902557.1738949051&semt=ais_hybrid&w=740"
            alt="Trading background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gray-900 bg-opacity-80"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="space-y-4 bg-white/90 backdrop-blur-sm rounded-2xl p-5 shadow-xl"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              variants={fadeIn}
            >
              <span className="inline-block text-sm font-semibold text-red-600 border-b-2 border-red-500 pb-1">
                Our Mission
              </span>
              <h2 className="text-3xl font-semibold text-gray-800 leading-tight">
              Simplifying Forex, Empowering Traders
              </h2>
              <p className="text-lg text-gray-600">
              We are on a mission to make Forex trading easier and more rewarding for everyone.
              Through smart automation and real-time insights, we help our users make better trading decisions with confidence.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gradient-to-r from-[#263283] to-[#ed2924] flex items-center justify-center">
                    <svg
                      className="h-4 w-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="ml-3 text-gray-700">
                  Deliver intelligent, hands-free trading solutions
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gradient-to-r from-[#263283] to-[#ed2924] flex items-center justify-center">
                    <svg
                      className="h-4 w-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="ml-3 text-gray-700">
                  Make Forex accessible to beginners and professionals
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gradient-to-r from-[#263283] to-[#ed2924] flex items-center justify-center">
                    <svg
                      className="h-4 w-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="ml-3 text-gray-700">
                  Create a trusted, growth-driven trading community
                  </p>
                </li>
              </ul>
            </motion.div>
            <motion.div
              className="  backdrop-blur-sm rounded-2xl  shadow-xl"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              variants={fadeIn}
            >
              <img src="/trading12.png" alt="" className="rounded-2xl " />
              </motion.div>

            {/* <motion.div
        className="bg-indigo-900/90 text-white backdrop-blur-sm p-8 rounded-2xl shadow-xl"
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        <h3 className="text-2xl font-bold mb-6">Why Choose FinRain?</h3>
        
        <div className="space-y-6">
          <div className="flex items-start">
            <div className="w-12 h-12 rounded-lg bg-indigo-700 flex items-center justify-center mr-4 flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h4 className="font-semibold text-xl mb-1">Smart Risk Management</h4>
              <p className="text-indigo-200">Our algorithms constantly monitor market conditions to minimize risks and optimize returns.</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="w-12 h-12 rounded-lg bg-indigo-700 flex items-center justify-center mr-4 flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h4 className="font-semibold text-xl mb-1">Lightning-Fast Execution</h4>
              <p className="text-indigo-200">Our platform executes trades at optimal speeds, capturing opportunities others miss.</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="w-12 h-12 rounded-lg bg-indigo-700 flex items-center justify-center mr-4 flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h4 className="font-semibold text-xl mb-1">Comprehensive Reports</h4>
              <p className="text-indigo-200">Track your performance with detailed analytics and insights to improve your strategy.</p>
            </div>
          </div>
        </div>
      </motion.div> */}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};
